import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { ChevronDown, InfoCircleFill } from "react-bootstrap-icons";
import { cents, showMoney } from "./Data/Money";
import { CostProjectionDto, ParkerCostDto } from "./CostProjectionSection";
import "./CostProjectionDetailsModal.scss";

const CHECKOUT_GRADIENT = "linear-gradient(135deg, #e8359a 0%, #c0116e 100%)";

export type CostProjectionDetailsSection = {
  title: string;
  data: CostProjectionDto;
  /** Drives default expand + pink header styling. */
  variant?: "current" | "upcoming";
};

type Props = {
  show: boolean;
  onHide: () => void;
  title: string;
  /** Single-period breakdown (enhanced current/next cards). */
  data?: CostProjectionDto;
  /** Combined breakdown (IsGroup upcoming). When set, overrides `data`. */
  sections?: CostProjectionDetailsSection[];
};

function parkerInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) {
    return parts[0].slice(0, Math.min(2, parts[0].length)).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function resolveVariant(
  section: CostProjectionDetailsSection,
  index: number,
  total: number,
): "current" | "upcoming" {
  if (section.variant) return section.variant;
  if (/current/i.test(section.title)) return "current";
  if (/upcoming|next/i.test(section.title)) return "upcoming";
  // Single section → treat as upcoming styling (neutral card)
  if (total === 1) return "upcoming";
  return index === 0 ? "current" : "upcoming";
}

function periodParkerCount(data: CostProjectionDto): number {
  return data.groups.reduce((sum, g) => sum + (g.parkerCount ?? 0), 0);
}

function periodSubtitle(data: CostProjectionDto, variant: "current" | "upcoming"): string {
  const count = periodParkerCount(data);
  const unit = count === 1 ? "parker" : "parkers";
  const when = variant === "current" ? "through today" : "next cycle";
  return `${count} ${unit} · ${when}`;
}

export function CostProjectionDetailsModal({
  show,
  onHide,
  title,
  data,
  sections,
}: Props) {
  const resolvedSections: CostProjectionDetailsSection[] =
    sections && sections.length > 0
      ? sections
      : data
        ? [{ title: "", data }]
        : [];

  const grandTotal = resolvedSections.reduce(
    (sum, s) => sum + (s.data.total.amount ?? 0),
    0,
  );
  const isCombined = Boolean(sections && sections.length > 0);
  const totalLabel = isCombined ? "Estimated total" : "Period total";

  const [expanded, setExpanded] = useState<Record<number, boolean>>({});

  useEffect(() => {
    if (!show) return;
    const both = resolvedSections.length >= 2;
    const next: Record<number, boolean> = {};
    resolvedSections.forEach((section, idx) => {
      const variant = resolveVariant(section, idx, resolvedSections.length);
      // Both periods: upcoming open, current collapsed. Single period: open.
      next[idx] = both ? variant === "upcoming" : true;
    });
    setExpanded(next);
    // Intentionally only when modal opens — avoid resetting mid-toggle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const toggle = (idx: number) => {
    setExpanded((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      contentClassName="cost-projection-details-modal"
    >
      <Modal.Header closeButton className="border-0 pb-0 pt-3 px-3">
        <div>
          <div className="cost-projection-details-eyebrow">Billing Forecast</div>
          <Modal.Title className="cost-projection-details-title">{title}</Modal.Title>
        </div>
      </Modal.Header>

      <Modal.Body className="pt-2 pb-3 px-3 d-flex flex-column gap-2">
        {resolvedSections.map((section, idx) => {
          const variant = resolveVariant(section, idx, resolvedSections.length);
          const isOpen = expanded[idx] ?? false;
          const amount = section.data.total.amount ?? 0;
          const heading = section.title || (variant === "current" ? "Current period activity" : "Upcoming period");

          return (
            <div
              key={idx}
              className={`cost-projection-period cost-projection-period--${variant}${isOpen ? " is-open" : ""}`}
            >
              <button
                type="button"
                className="cost-projection-period__header"
                onClick={() => toggle(idx)}
                aria-expanded={isOpen}
              >
                <span
                  className={`cost-projection-period__dot${isOpen ? " is-selected" : ""}`}
                  aria-hidden
                />
                <span className="cost-projection-period__text min-w-0 flex-grow-1 text-start">
                  <span className="cost-projection-period__title">{heading}</span>
                  <span className="cost-projection-period__subtitle">
                    {periodSubtitle(section.data, variant)}
                  </span>
                </span>
                <span
                  className={`cost-projection-period__amount${isOpen ? " is-selected" : ""}`}
                >
                  {showMoney(cents(amount))}
                </span>
                <ChevronDown
                  className={`cost-projection-period__chevron${isOpen ? " is-open" : ""}`}
                  aria-hidden
                />
              </button>

              {isOpen && (
                <div className="cost-projection-period__body">
                  {variant === "current" && (
                    <div className="alert alert-muted-gold d-flex align-items-start gap-2 mb-0 small" role="alert">
                      <InfoCircleFill className="flex-shrink-0 mt-1" />
                      <span>
                        These are charges incurred after the period started or payment was made and will be billed on the next cycle.
                      </span>
                    </div>
                  )}
                  <PeriodGroups data={section.data} />
                </div>
              )}
            </div>
          );
        })}

        <div
          className="cost-projection-grand-total"
          style={{ background: CHECKOUT_GRADIENT }}
        >
          <span>{totalLabel}</span>
          <span>{showMoney(cents(grandTotal))}</span>
        </div>
      </Modal.Body>
    </Modal>
  );
}

function PeriodGroups({ data }: { data: CostProjectionDto }) {
  const groups = data.groups.filter((group) => group.parkerCount > 0);
  if (groups.length === 0) {
    return <div className="cost-projection-period__empty text-muted small">No line items for this period.</div>;
  }

  return (
    <div className="cost-projection-period__groups">
      {groups.map((group, gIdx) => {
        const hasParkerCosts = group.parkerCosts?.length > 0;
        const unitLabel = group.groupId === null ? "space" : "parker";
        const unitLabelPlural = `${unitLabel}${group.parkerCount !== 1 ? "s" : ""}`;

        return (
          <div
            key={group.groupId ?? `unallocated-${gIdx}`}
            className="cost-projection-section"
          >
            <div className="cost-projection-section__header">
              <div className="d-flex align-items-center gap-2 min-w-0">
                <span className="cost-projection-section__label">{group.groupName}</span>
                <span className="cost-projection-section__pill">
                  {group.parkerCount} {unitLabelPlural}
                </span>
              </div>
              <span className="cost-projection-section__total">
                {showMoney(cents(group.subtotal))}
              </span>
            </div>

            <div className="cost-projection-section__body">
              {hasParkerCosts ? (
                group.parkerCosts.map((pc) => (
                  <ParkerCostRow key={pc.subscriptionParkerId} parkerCost={pc} />
                ))
              ) : (
                (() => {
                  const baseLineItem = group.lineItems.find(li => li.description === "Base Price");
                  const unitPrice = baseLineItem?.unitPrice;
                  return unitPrice ? (
                    <div className="cost-projection-section__fallback small text-muted">
                      <span className="fw-semibold text-dark">
                        {group.parkerCount} space{group.parkerCount !== 1 ? "s" : ""}
                      </span>
                      <span className="ms-1">× {showMoney(cents(unitPrice))} each</span>
                    </div>
                  ) : null;
                })()
              )}

              {group.appliedTiers?.length > 0 && (
                <div className="cost-projection-section__tiers">
                  {group.appliedTiers.map((tier: any, idx: number) => (
                    <div
                      key={idx}
                      className="d-flex justify-content-between text-dark small"
                    >
                      <div className="fw-semibold">
                        {tier.label}
                        <span className="text-muted ms-1">({tier.quantity})</span>
                      </div>
                      <div className={`fw-semibold ${tier.total < 0 ? "text-success" : ""}`}>
                        {showMoney(cents(tier.total))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ParkerCostRow({ parkerCost }: { parkerCost: ParkerCostDto }) {
  const { parkerName, startDate, finalPrice, wasProrated } = parkerCost;

  return (
    <div className="cost-projection-parker-row">
      <div className="cost-projection-parker-avatar" aria-hidden>
        {parkerInitials(parkerName)}
      </div>
      <div className="cost-projection-parker-meta flex-grow-1 min-w-0">
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <span className="fw-semibold text-dark">{parkerName}</span>
          {wasProrated && (
            <span className="cost-projection-prorated-badge">Prorated</span>
          )}
        </div>
        {wasProrated && startDate && (
          <div className="cost-projection-parker-start">Starts {startDate}</div>
        )}
      </div>
      <div className="text-end flex-shrink-0">
        <div className="fw-bold text-dark">{showMoney(cents(finalPrice))}</div>
      </div>
    </div>
  );
}
